import { useCallback } from 'react';
import classnames from 'clsx';
import { useDispatch, useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';

import { NoteForType } from './note-for-type';
import style from './style.module.less';
import {
  useDeprecatedLocalStorage,
  generateUserSpecificPersistentKey,
  USER_ORDER_DETAILS_PINNED_NOTES,
} from '../../hooks/use-local-storage';
import { getAllNoteTypes } from '../../services/order-note-service';
import { updateFulfillmentPlanNoteRequest } from '../../store/entities/entities.actions';
import Global from '../../store/global-selectors';

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {string} [props.className]
 * @param {string} [props.labelClassName]
 * @param {boolean} [props.hideUnpinned]
 * @param {string} [props.containerClassName]
 * @returns {JSX.Element}
 */
export const FulfillmentPlanNotes = ({ fulfillmentPlanId, className, labelClassName, hideUnpinned = false, containerClassName }) => {
  const dispatch = useDispatch();
  const notes =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId,
        property: 'notes',
      }),
    ) || emptyArray;
  const userId = useSelector(Global.selectUserCurrentUserId);
  const [pinnedNoteTypes, setPinnedNoteTypes] = useDeprecatedLocalStorage(
    generateUserSpecificPersistentKey(USER_ORDER_DETAILS_PINNED_NOTES, userId),
    emptyArray,
  );

  const noteTypes = getAllNoteTypes();

  const pinNoteType = useCallback(
    (noteType) => {
      setPinnedNoteTypes([...pinnedNoteTypes, noteType]);
    },
    [pinnedNoteTypes, setPinnedNoteTypes],
  );

  const unpinNoteType = useCallback(
    (noteType) => {
      setPinnedNoteTypes(pinnedNoteTypes.filter((nt) => nt !== noteType));
    },
    [pinnedNoteTypes, setPinnedNoteTypes],
  );

  const updateNote = useCallback(
    (note) => dispatch(updateFulfillmentPlanNoteRequest({ fulfillmentPlanId, note })),
    [dispatch, fulfillmentPlanId],
  );

  const pinnedNoteObjs = noteTypes
    .filter((noteType) => pinnedNoteTypes.includes(noteType.id))
    .map((noteType) => {
      const noteBody = notes.find((note) => note.type === noteType.id)?.body ?? '';
      return { noteType, noteBody };
    });

  const unpinnedNoteObjs = noteTypes
    .filter((noteType) => !pinnedNoteTypes.includes(noteType.id))
    .map((noteType) => {
      const noteBody = notes.find((note) => note.type === noteType.id)?.body ?? '';
      return { noteType, noteBody };
    });

  if (hideUnpinned && pinnedNoteObjs.length === 0) {
    return null;
  }

  return (
    <table className={classnames(style['fulfillment-plan-notes-widget'], containerClassName)}>
      <tbody key='pinned notes'>
        {pinnedNoteObjs.map(({ noteType, noteBody }) => (
          <NoteForType
            key={noteType.id}
            className={className}
            labelClassName={labelClassName}
            noteBody={noteBody}
            noteType={noteType}
            pinned
            updateNote={updateNote}
            updatePinnedNotes={unpinNoteType}
          />
        ))}
      </tbody>
      {!hideUnpinned && (
        <tbody key='unpinned notes'>
          {unpinnedNoteObjs.map(({ noteType, noteBody }) => (
            <NoteForType
              key={noteType.id}
              className={className}
              labelClassName={labelClassName}
              noteBody={noteBody}
              noteType={noteType}
              updateNote={updateNote}
              updatePinnedNotes={pinNoteType}
            />
          ))}
        </tbody>
      )}
    </table>
  );
};
